
import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Globe, Settings, RefreshCw, Plus, Edit3, Trash2, Eye, EyeOff, Save, X, Info, PieChart, Activity } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useETF, ETF, ETFInput, ETFHolding } from '@/shared/hooks/useETF';
import { useUserETF } from '@/shared/hooks/useUserETF';
import { seedETFs } from '@/shared/utils/seedETFs';
import { db } from '@/lib/firebase';
import { formatDisplayName } from '@/shared/utils/formatters';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const AdminETFPage: React.FC = () => {
    const { etfs, loading, addETF, updateETF, deleteETF } = useETF();
    const { allPositions, sellETF, loading: userETFLoading } = useUserETF();
    const [activeTab, setActiveTab] = useState<'products' | 'positions'>('products');
    const [showModal, setShowModal] = useState(false);
    const [editingETF, setEditingETF] = useState<ETF | null>(null);
    const [saving, setSaving] = useState(false);
    const [seeded, setSeeded] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

    // Form state
    const [form, setForm] = useState<ETFInput>({
        name: '',
        ticker: '',
        description: '',
        basePrice: 0,
        currentPrice: 0,
        roi: 0,
        aum: 0,
        category: 'Index',
        holdings: [],
        status: 'Active',
        vitals: [0, 0, 0, 0, 0]
    });

    const [holdingInput, setHoldingInput] = useState({ asset: '', weight: 0 });

    // Initial seeding
    useEffect(() => {
        if (!loading && etfs.length === 0 && !seeded) {
            seedETFs().then(res => {
                if (res) setSeeded(true);
            });
        }
    }, [loading, etfs.length, seeded]);

    // ROI Auto-calculation
    useEffect(() => {
        if (form.basePrice > 0) {
            const calculatedRoi = ((form.currentPrice - form.basePrice) / form.basePrice) * 100;
            setForm(prev => ({ ...prev, roi: Number(calculatedRoi.toFixed(2)) }));
        }
    }, [form.basePrice, form.currentPrice]);

    const openAddModal = () => {
        setEditingETF(null);
        setForm({
            name: '', ticker: '', description: '', basePrice: 0, currentPrice: 0,
            roi: 0, aum: 0, category: 'Index', holdings: [], status: 'Active', vitals: [0, 0, 0, 0, 0]
        });
        setShowModal(true);
    };

    const openEditModal = (etf: ETF) => {
        setEditingETF(etf);
        setForm({
            name: etf.name, ticker: etf.ticker, description: etf.description,
            basePrice: etf.basePrice, currentPrice: etf.currentPrice,
            roi: etf.roi, aum: etf.aum, category: etf.category,
            holdings: etf.holdings || [], status: etf.status, vitals: etf.vitals || [0, 0, 0, 0, 0]
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.name || !form.ticker || form.basePrice <= 0) {
            alert('필수 정보를 입력해주세요 (상품명, 티커, 기준가)');
            return;
        }
        setSaving(true);
        try {
            if (editingETF) {
                await updateETF(editingETF.id, form);
            } else {
                await addETF(form);
            }
            setShowModal(false);
        } catch (e: any) {
            alert('저장 실패: ' + e.message);
        }
        setSaving(false);
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('정말 삭제하시겠습니까?')) {
            await deleteETF(id);
            setDeleteConfirm(null);
        }
    };

    const toggleStatus = async (etf: ETF) => {
        const newStatus = etf.status === 'Active' ? 'Inactive' : 'Active';
        await updateETF(etf.id, { status: newStatus });
    };

    const addHolding = () => {
        if (holdingInput.asset && holdingInput.weight > 0) {
            setForm(prev => ({ ...prev, holdings: [...prev.holdings, { ...holdingInput }] }));
            setHoldingInput({ asset: '', weight: 0 });
        }
    };

    const removeHolding = (index: number) => {
        setForm(prev => ({ ...prev, holdings: prev.holdings.filter((_, i) => i !== index) }));
    };

    const handleAdminSell = async (pos: any) => {
        const etf = etfs.find(e => e.id === pos.id);
        const currentPrice = etf?.currentPrice || pos.avgPrice;

        if (!window.confirm(`[관리자] 해당 유저(${pos.userId})의 ${pos.name} 포지션을 강제 환매하시겠습니까?\n환급 예정액: ₩${(pos.quantity * currentPrice).toLocaleString()}`)) {
            return;
        }

        try {
            await sellETF(pos.id, pos.quantity, currentPrice, pos.userId);
            await addDoc(collection(db, 'transactions'), {
                userId: pos.userId,
                userName: `Admin Force Sell (${pos.userId})`,
                type: 'ETF Sell (Admin)',
                asset: pos.ticker,
                amount: (pos.quantity * currentPrice).toString(),
                price: currentPrice.toString(),
                status: 'Completed',
                createdAt: serverTimestamp()
            });
            alert('강제 환매 처리가 완료되었습니다.');
        } catch (e: any) {
            alert('강제 환매 처리 중 오류가 발생했습니다: ' + e.message);
        }
    };

    if (loading) {
        return <div className="flex items-center justify-center py-20"><RefreshCw className="h-10 w-10 text-emerald-500 animate-spin" /></div>;
    }

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-1">
                    <h1 className="text-2xl font-black flex items-center gap-3">
                        <BarChart3 className="h-7 w-7 text-emerald-500" /> ETF 마켓 관리
                    </h1>
                    <p className="text-slate-500 font-bold text-sm uppercase tracking-widest">글로벌 ETF 지수 관리 센터</p>
                </div>
                <div className="flex items-center gap-3 p-1 bg-slate-900 rounded-2xl border border-slate-800">
                    <button
                        onClick={() => setActiveTab('products')}
                        className={cn("px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all", activeTab === 'products' ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/20" : "text-slate-500 hover:text-white")}
                    >
                        상품 목록 관리
                    </button>
                    <button
                        onClick={() => setActiveTab('positions')}
                        className={cn("px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all", activeTab === 'positions' ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/20" : "text-slate-500 hover:text-white")}
                    >
                        유저 보유 정보
                    </button>
                </div>
                {activeTab === 'products' && (
                    <button onClick={openAddModal} className="h-12 px-6 rounded-2xl bg-emerald-600 text-white font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-emerald-500 transition-all shadow-xl shadow-emerald-600/20">
                        <Plus className="h-4 w-4" /> 상품 등록
                    </button>
                )}
            </div>

            {/* Stats Header */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                    { label: '활성 상품 수', value: etfs.filter(e => e.status === 'Active').length, icon: Globe, color: 'text-indigo-500' },
                    { label: '평균 수익률', value: `${(etfs.reduce((s, e) => s + e.roi, 0) / (etfs.length || 1)).toFixed(2)}%`, icon: TrendingUp, color: 'text-emerald-500' },
                    { label: '총 운용 규모 (AUM)', value: `₩${(etfs.reduce((s, e) => s + e.aum, 0) / 100000000).toFixed(1)}억`, icon: Activity, color: 'text-blue-500' },
                    { label: '운용 상태', value: 'Live', icon: RefreshCw, color: 'text-amber-500' },
                ].map((stat, i) => (
                    <div key={i} className="p-6 rounded-3xl bg-slate-950 border border-slate-800 space-y-3">
                        <div className={cn("w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center", stat.color)}>
                            <stat.icon className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{stat.label}</p>
                            <p className="text-xl font-black text-white">{stat.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Content Based on Tab */}
            {activeTab === 'products' ? (
                <div className="rounded-[2.5rem] bg-slate-950 border border-slate-800 overflow-hidden shadow-2xl">
                    <div className="px-8 py-6 border-b border-slate-800 flex items-center justify-between bg-white/[0.02]">
                        <h3 className="font-bold text-sm uppercase tracking-widest text-slate-300">ETF 상품 리스트</h3>
                        <div className="flex items-center gap-2 text-emerald-500">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                            <span className="text-[10px] font-black uppercase tracking-widest">실시간 연동 리스트</span>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-[#0f0f12] text-slate-500 font-black uppercase tracking-widest border-b border-slate-800">
                                <tr>
                                    <th className="px-8 py-4 text-left">상품정보</th>
                                    <th className="px-8 py-4 text-left">테마/지수</th>
                                    <th className="px-8 py-4 text-right">수익률</th>
                                    <th className="px-8 py-4 text-right">기준가/현재가</th>
                                    <th className="px-8 py-4 text-right">운용액(AUM)</th>
                                    <th className="px-8 py-4 text-center">액션</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800">
                                {etfs.map((etf) => (
                                    <tr key={etf.id} className="hover:bg-white/[0.01] transition-colors group">
                                        <td className="px-8 py-5">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 font-black text-xs uppercase">{etf.ticker.slice(0, 1)}</div>
                                                <div>
                                                    <p className="font-bold text-white group-hover:text-emerald-400 transition-colors">{etf.name}</p>
                                                    <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">{etf.ticker}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <span className="px-2 py-0.5 rounded-md bg-white/5 text-slate-400 text-[9px] font-bold uppercase border border-slate-800">{etf.category}</span>
                                        </td>
                                        <td className="px-8 py-5 text-right">
                                            <div className={cn("inline-flex items-center gap-1 font-black px-2 py-1 rounded-lg", etf.roi >= 0 ? "text-emerald-500 bg-emerald-500/5" : "text-rose-500 bg-rose-500/5")}>
                                                {etf.roi >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingUp className="h-3 w-3 rotate-180" />}
                                                {etf.roi}%
                                            </div>
                                        </td>
                                        <td className="px-8 py-5 text-right font-bold text-white">
                                            <p className="text-[10px] text-slate-500">기준: ₩{etf.basePrice?.toLocaleString()}</p>
                                            <p>현재: ₩{etf.currentPrice?.toLocaleString()}</p>
                                        </td>
                                        <td className="px-8 py-5 text-right font-black text-slate-400 uppercase tracking-tighter">
                                            ₩{(etf.aum / 100000000).toFixed(1)}억
                                        </td>
                                        <td className="px-8 py-5">
                                            <div className="flex items-center justify-center gap-2">
                                                <button onClick={() => toggleStatus(etf)} title={etf.status === 'Active' ? '비활성화' : '활성화'} className={cn("h-9 w-9 rounded-xl border flex items-center justify-center transition-all", etf.status === 'Active' ? "border-emerald-500/20 text-emerald-500 bg-emerald-500/5" : "border-slate-800 text-slate-500 bg-slate-900")}>
                                                    {etf.status === 'Active' ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                                </button>
                                                <button onClick={() => openEditModal(etf)} className="h-9 w-9 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-500 hover:text-white transition-all">
                                                    <Edit3 className="h-4 w-4" />
                                                </button>
                                                <button onClick={() => handleDelete(etf.id)} className="h-9 w-9 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-500 hover:bg-rose-500 hover:text-white transition-all">
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="rounded-[2.5rem] bg-slate-950 border border-slate-800 overflow-hidden shadow-2xl">
                    <div className="px-8 py-6 border-b border-slate-800 flex items-center justify-between bg-white/[0.02]">
                        <h3 className="font-bold text-sm uppercase tracking-widest text-slate-300">유저 ETF 보유 포지션 관리</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-[#0f0f12] text-slate-500 font-black uppercase tracking-widest border-b border-slate-800">
                                <tr>
                                    <th className="px-8 py-4 text-left">유저 ID</th>
                                    <th className="px-8 py-4 text-left">보유 상품</th>
                                    <th className="px-8 py-4 text-right">보유 수량</th>
                                    <th className="px-8 py-4 text-right">투자 원금</th>
                                    <th className="px-8 py-4 text-right">투자 평단가</th>
                                    <th className="px-8 py-4 text-center">액션</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800">
                                {allPositions.map((pos, i) => (
                                    <tr key={`${pos.userId}-${pos.id}-${i}`} className="hover:bg-white/[0.01] transition-colors">
                                        <td className="px-8 py-5 font-bold text-slate-300">{formatDisplayName({ id: pos.userId })}</td>
                                        <td className="px-8 py-5">
                                            <p className="font-bold text-white">{pos.name}</p>
                                            <p className="text-[10px] text-slate-500 uppercase tracking-widest">{pos.ticker}</p>
                                        </td>
                                        <td className="px-8 py-5 text-right font-black text-white tabular-nums">{pos.quantity.toFixed(4)}</td>
                                        <td className="px-8 py-5 text-right font-bold text-slate-400">₩{pos.investedAmount.toLocaleString()}</td>
                                        <td className="px-8 py-5 text-right font-bold text-slate-400">₩{pos.avgPrice.toLocaleString()}</td>
                                        <td className="px-8 py-5 text-center">
                                            <button
                                                onClick={() => handleAdminSell(pos)}
                                                className="px-4 py-2 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-500 text-[10px] font-black uppercase tracking-widest hover:bg-orange-500 hover:text-white transition-all shadow-lg shadow-orange-500/5"
                                            >
                                                강제 환매
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {allPositions.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-8 py-20 text-center text-slate-600 font-black uppercase tracking-[0.3em]">보유 내역이 없습니다.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-slate-950 border border-slate-800 rounded-[2.5rem] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-10 shadow-2xl space-y-8 animate-in zoom-in-95 duration-300">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-black text-white uppercase tracking-wider flex items-center gap-3">
                                {editingETF ? <><Edit3 className="h-6 w-6 text-emerald-500" /> 상품 정보 수정</> : <><Plus className="h-6 w-6 text-emerald-500" /> 신규 상품 등록</>}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-500 hover:text-white transition-all">
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 block">상품명 *</label>
                                    <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-2xl px-4 text-sm font-bold text-white focus:outline-none focus:border-emerald-500/50 transition-all placeholder:text-slate-700" placeholder="예: 메타버스 리더스 ETF" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 block">티커 (Ticker) *</label>
                                    <input value={form.ticker} onChange={e => setForm(p => ({ ...p, ticker: e.target.value.toUpperCase() }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-2xl px-4 text-sm font-bold text-white focus:outline-none focus:border-emerald-500/50 transition-all" placeholder="예: META-TOP10" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 block">테마 카테고리</label>
                                    <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-2xl px-4 text-sm font-bold text-white focus:outline-none focus:border-emerald-500/50 transition-all">
                                        <option value="Index">지수 추종 (Index)</option>
                                        <option value="Leverage">레버리지 (Leverage)</option>
                                        <option value="Theme">테마 주도 (Theme)</option>
                                        <option value="Sector">특정 섹터 (Sector)</option>
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 block">기준가 (원)</label>
                                        <input type="number" value={form.basePrice} onChange={e => setForm(p => ({ ...p, basePrice: Number(e.target.value) }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-2xl px-4 text-sm font-bold text-white focus:outline-none focus:border-emerald-500/50 transition-all" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 block">현재가 (원)</label>
                                        <input type="number" value={form.currentPrice} onChange={e => setForm(p => ({ ...p, currentPrice: Number(e.target.value) }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-2xl px-4 text-sm font-bold text-white focus:outline-none focus:border-emerald-500/50 transition-all" />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 block">수익률 (ROI) - 자동계산</label>
                                    <div className={cn("w-full h-12 bg-slate-900 border border-slate-800 rounded-2xl px-4 flex items-center text-sm font-black", form.roi >= 0 ? "text-emerald-500" : "text-rose-500")}>
                                        {form.roi > 0 ? '+' : ''}{form.roi}%
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 block">운용액 (AUM, 원)</label>
                                    <input type="number" value={form.aum} onChange={e => setForm(p => ({ ...p, aum: Number(e.target.value) }))} className="w-full h-12 bg-slate-900 border border-slate-800 rounded-2xl px-4 text-sm font-bold text-white focus:outline-none focus:border-emerald-500/50 transition-all" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 block">상품 설명</label>
                            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full h-24 bg-slate-900 border border-slate-800 rounded-2xl px-4 py-3 text-sm font-bold text-white focus:outline-none focus:border-emerald-500/50 transition-all resize-none" placeholder="상품에 대한 상세 설명을 입력하세요" />
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                                    <PieChart className="h-4 w-4 text-emerald-500" /> 구성 자산 비중
                                </label>
                                <div className="text-[10px] font-black text-slate-600 uppercase">
                                    합계: {form.holdings.reduce((s, h) => s + h.weight, 0)}%
                                </div>
                            </div>
                            <div className="flex gap-3 mb-4">
                                <input placeholder="자산명 (예: BTC)" value={holdingInput.asset} onChange={e => setHoldingInput(prev => ({ ...prev, asset: e.target.value }))} className="flex-1 h-11 bg-slate-900 border border-slate-800 rounded-xl px-4 text-xs font-bold text-white outline-none focus:border-emerald-500/30" />
                                <input type="number" placeholder="비중 (%)" value={holdingInput.weight || ''} onChange={e => setHoldingInput(prev => ({ ...prev, weight: Number(e.target.value) }))} className="w-24 h-11 bg-slate-900 border border-slate-800 rounded-xl px-4 text-xs font-bold text-white outline-none focus:border-emerald-500/30" />
                                <button onClick={addHolding} className="px-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-[10px] font-black uppercase transition-all">추가</button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {form.holdings.map((h, i) => (
                                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/5 group/tag">
                                        <span className="text-[10px] font-bold text-white uppercase">{h.asset}</span>
                                        <span className="text-[10px] font-black text-emerald-500">{h.weight}%</span>
                                        <button onClick={() => removeHolding(i)} className="text-slate-600 hover:text-rose-500 transition-colors"><X className="h-3 w-3" /></button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex gap-4 pt-4">
                            <button onClick={() => setShowModal(false)} className="flex-1 h-14 rounded-2xl bg-slate-950 border border-slate-800 text-slate-500 font-black text-xs uppercase tracking-widest hover:bg-slate-900 transition-all">
                                취소
                            </button>
                            <button onClick={handleSave} disabled={saving} className="flex-1 h-14 rounded-2xl bg-emerald-600 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-emerald-500 transition-all shadow-xl shadow-emerald-600/20 disabled:opacity-50">
                                <Save className="h-4 w-4" />
                                {saving ? '저장 중...' : (editingETF ? '수정 완료' : '상품 등록')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminETFPage;
